// QuickSearch — recherche ⌘K / Ctrl+K des pages de pikachu (refonte 10/2026). Purement additive :
// elle liste les entrées du menu (et leurs sous-pages) et y navigue ; aucune page n'est modifiée.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { dashboardAll } from "../routes/index";

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const flatten = () => {
  const out = [];
  dashboardAll.forEach((r) => {
    if (r.children && r.children.length) {
      r.children.forEach((c) => c.path && out.push({ label: c.name, section: r.name, path: c.path }));
    } else if (r.path) {
      out.push({ label: r.name, section: "", path: r.path });
    }
  });
  return out;
};

function QuickSearch({ history }) {
  const pages = useMemo(flatten, []);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        if (inputRef.current) inputRef.current.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => {
    const t = norm(q).trim();
    if (!t) return pages.slice(0, 8);
    return pages.filter((p) => norm(p.label + " " + p.section).includes(t)).slice(0, 8);
  }, [q, pages]);

  const go = (p) => {
    setOpen(false);
    setQ("");
    if (inputRef.current) inputRef.current.blur();
    history.push(p.path);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === "Enter" && results[cursor]) { e.preventDefault(); go(results[cursor]); }
    else if (e.key === "Escape") { setOpen(false); if (inputRef.current) inputRef.current.blur(); }
  };

  return (
    <div className="trc-quicksearch">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
      <input
        ref={inputRef}
        type="search"
        value={q}
        placeholder="Rechercher une page de pikachu…"
        aria-label="Rechercher une page"
        onChange={(e) => { setQ(e.target.value); setCursor(0); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
      />
      <kbd>⌘K</kbd>
      {open && results.length > 0 ? (
        <ul className="trc-quicksearch-results" role="listbox">
          {results.map((p, i) => (
            <li key={p.path + p.label} role="option" aria-selected={i === cursor}
                className={i === cursor ? "on" : ""}
                onMouseDown={(e) => { e.preventDefault(); go(p); }}
                onMouseEnter={() => setCursor(i)}>
              <span>{p.label}</span>
              {p.section ? <small>{p.section}</small> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default withRouter(QuickSearch);
